import React, { useCallback, useMemo, useState } from "react";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import styles from "./RemoveOrgFromAccountModal.module.scss";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { IOrganizationStore } from "../../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../../services/IOrganizationStore";
import { observer } from "mobx-react-lite";
import { AsyncSelect } from "@whatslly/ui-components/v2/BaseElements/Select";
import type { IAccountOrganizationsStore } from "../IAccountOrganizationsStore";
import { InjectAccountOrganizationsStore } from "../IAccountOrganizationsStore";
type AssignOrgsToAccountModalProps = IModalInstance<{
  orgIds: string[];
}>;

export const AssignOrgsToAccountModal: React.FC<AssignOrgsToAccountModalProps> = observer(({ closeModal }) => {
  const { t } = useTranslation();
  const organizationStore = useDependencyInjector<IOrganizationStore>(InjectOrganizationStore);
  const store = useDependencyInjector<IAccountOrganizationsStore>(InjectAccountOrganizationsStore);
  const [selectedOrgs, setSelectedOrgs] = useState<{ label: string; value: string }[]>([]);
  const accountOrgs = useMemo(() => (store.orgs.state === "fulfilled" ? store.orgs.value : undefined), [store.orgs.value]);
  const searchOrgs = useCallback(
    async (searchValue: string) => {
      const { options } = await organizationStore.searchOrgs(searchValue);
      return {
        options: options.filter((org) => !accountOrgs?.map((org_1) => org_1.id).includes(org.value)),
      };
    },
    [accountOrgs, organizationStore]
  );

  return (
    <BaseModal
      className={classnames(styles.modal)}
      bodyClassName={styles.body}
      closeOnClickOutside={false}
      closeOnEscape={true}
      closeModal={() => closeModal()}
      testId={"assign-orgs-modal"}
      header={<ModalHeader closeModal={() => closeModal()}>{t(`account_admin.account_orgs.assign_orgs_modal.title`)}</ModalHeader>}
    >
      <div className={styles.text}>{t(`account_admin.account_orgs.assign_orgs_modal.description`)}</div>
      <div className={styles.orgsSelector}>
        <AsyncSelect
          isMulti={true}
          hideDropdownIndicator
          menuWidth="500px"
          data-testid="org-select"
          borderColor="none"
          selectSize={"small"}
          controlClassName={styles.selectControl}
          loadOptionsFromServerFunction={searchOrgs}
          placeholder={t("search_orgs.search_orgs")}
          value={selectedOrgs}
          onChange={setSelectedOrgs}
        />
      </div>
      <ModalButtons
        testId={"modal-buttons-submit-button"}
        onCancel={() => closeModal()}
        isSubmitDisabled={selectedOrgs.length === 0}
        onSubmit={() =>
          closeModal({
            orgIds: selectedOrgs.map((org) => org.value),
          })
        }
      />
    </BaseModal>
  );
});
